import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { translateNewsItem } from "@/lib/translate.functions";
import { sendNewsToTelegram } from "@/lib/telegram.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Languages, Send } from "lucide-react";

export const Route = createFileRoute("/_authenticated/news")({ component: Page });

function Page() {
  const qc = useQueryClient();
  const tr = useServerFn(translateNewsItem);
  const sd = useServerFn(sendNewsToTelegram);
  const { data } = useQuery({
    queryKey: ["news_items"],
    queryFn: async () => (await supabase.from("news_items").select("*, asset_categories(name_th, icon), translated_news(title_th)").order("created_at", { ascending: false }).limit(100)).data ?? [],
  });
  const doTr = async (id: string) => {
    try { toast.loading("กำลังแปล...", { id: "tr" }); await tr({ data: { newsItemId: id } }); toast.success("แปลสำเร็จ", { id: "tr" }); qc.invalidateQueries({ queryKey: ["news_items"] }); }
    catch (e: any) { toast.error(e.message, { id: "tr" }); }
  };
  const doSend = async (id: string) => {
    try { toast.loading("กำลังส่ง...", { id: "sd" }); const r: any = await sd({ data: { newsItemId: id } }); toast.success(`ส่ง ${r.success}/${r.total}`, { id: "sd" }); qc.invalidateQueries({ queryKey: ["news_items"] }); }
    catch (e: any) { toast.error(e.message, { id: "sd" }); }
  };
  const all = data ?? [];
  const translated = all.filter((n: any) => n.translated_news?.[0]?.title_th);
  const original = all.filter((n: any) => !n.translated_news?.[0]?.title_th);
  const renderTable = (rows: any[], mode: "original" | "translated") => (
    <Table>
      <TableHeader><TableRow><TableHead>หัวข้อ</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead>Approval</TableHead><TableHead>เวลา</TableHead><TableHead className="text-right">การกระทำ</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((n: any) => (
          <TableRow key={n.id}>
            <TableCell className="max-w-md">
              {mode === "translated"
                ? (<><div className="font-medium truncate text-primary">{n.translated_news?.[0]?.title_th}</div><div className="text-xs text-muted-foreground truncate">EN: {n.original_title}</div></>)
                : (<div className="font-medium truncate">{n.original_title}</div>)}
            </TableCell>
            <TableCell className="text-xs">{n.asset_categories?.icon} {n.asset_categories?.name_th ?? "—"}</TableCell>
            <TableCell><Badge variant="outline">{n.status}</Badge></TableCell>
            <TableCell><Badge>{n.approval_status}</Badge></TableCell>
            <TableCell className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString("th-TH")}</TableCell>
            <TableCell className="text-right space-x-1">
              {mode === "original" && <Button size="sm" variant="outline" onClick={() => doTr(n.id)}><Languages className="h-3 w-3 mr-1"/>แปล</Button>}
              <Button size="sm" variant="outline" onClick={() => doSend(n.id)}><Send className="h-3 w-3 mr-1"/>ส่ง</Button>
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">ไม่มีข้อมูล</TableCell></TableRow>}
      </TableBody>
    </Table>
  );
  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-display font-semibold">News Items</h1><p className="text-sm text-muted-foreground">ข่าวทั้งหมดในระบบ — แปลและส่งด้วยตนเองได้</p></div>
      <Card>
        <CardHeader><CardTitle>รายการข่าว</CardTitle><CardDescription>{all.length} รายการ (ล่าสุด 100)</CardDescription></CardHeader>
        <CardContent>
          <Tabs defaultValue="original" className="w-full">
            <TabsList>
              <TabsTrigger value="original">ภาษาเดิม ({original.length})</TabsTrigger>
              <TabsTrigger value="translated">แปลไทยแล้ว ({translated.length})</TabsTrigger>
              <TabsTrigger value="all">ทั้งหมด ({all.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="original" className="mt-4">{renderTable(original, "original")}</TabsContent>
            <TabsContent value="translated" className="mt-4">{renderTable(translated, "translated")}</TabsContent>
            <TabsContent value="all" className="mt-4">{renderTable(all, "original")}</TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}